import defaultCombiningPercents from '../trainer/combiningPercents.json';
import { chunk, cloneDeep, sample, random, shuffle, difference, map, keys, values } from 'lodash';
import BigDecimal from '../trainer/BigDecimal';

const MIN_ARR_LGTH_OF_DIGIT_CAP = 2;
const EXTRA_WEIGHT = 0.3;

export default {
    data() {
        return {
            bracketCounter: 0,
        };
    },
    methods: {
        generateExample() {
            let numberChunks = this.generateNumbers();
            if (this.parameters.bracketsEnabled) {
                numberChunks = this.addBrackets(numberChunks);
            }
            if (!this.multOrDivChosen) {
                numberChunks = shuffle(numberChunks);
            }

            let tree = this.onlyDivisionChosen
                ? this.convertToDivisionOnlyTree(numberChunks)
                : this.convertToTree(numberChunks);

            if (
                (this.parameters.positiveAnswers && !this.parameters.negativeAnswers && tree.result.isNegative()) ||
                (!this.parameters.positiveAnswers && this.parameters.negativeAnswers && !tree.result.isNegative()) ||
                new BigDecimal(tree.result.toFixed()).isZero()
            ) {
                this.generateExample();
                return;
            }

            this.example = tree.toString();

            this.rightAnswer = this.parameters.fractions
                ? this.removeTrailingZeros(tree.result.toFixed(this.parameters.fractionalPart))
                : tree.result.toString();
        },
        generateNumbers() {
            let numbers = [];
            const fractionalDivisionOnlyNumber = this.onlyDivisionChosen && this.parameters.fractions;
            for (let i = 0; i < this.parameters.numberAmount; i++) {
                const isFirstNumber = i === 0;
                if (!(isFirstNumber && fractionalDivisionOnlyNumber)) {
                    numbers.push(isFirstNumber ? this.generateFirstNumber() : this.generateNumber());
                }
            }
            if (fractionalDivisionOnlyNumber) {
                numbers.unshift(this.generateFirstNumber(numbers));
            }
            return numbers;
        },
        generateNumber() {
            let maxDigitCapacity = this.getCapacityByDifficult;
            const combiningEnabled = this.parameters.combining || this.parameters.multDivDigitCapacity.right.length > 1;
            if (combiningEnabled) {
                let changedCombiningPercents = this.changeCombiningPercents();

                maxDigitCapacity = this.generateCurrentDigitsLimit(maxDigitCapacity, changedCombiningPercents);
            }
            let number = this.getNumberByCapacity(maxDigitCapacity);
            if (this.parameters.fractions) {
                number = number.add(this.generateFractionalPart());
            }
            return number;
        },
        generateFirstNumber(numbers = null) {
            if (this.onlyDivisionChosen && this.parameters.fractions) {
                const resultCapacity = sample(this.parameters.multDivDigitCapacity.right);
                const numberCapacitiesSum = numbers.reduce((sumResult, number) => {
                    const numberCapacity = +number
                        .toString()
                        .replace(/-/, '')
                        .replace(/\.\d*/, '').length;

                    return sumResult + numberCapacity;
                }, resultCapacity);
                let number = this.getNumberByCapacity(numberCapacitiesSum);
                if (this.parameters.fractions) {
                    number = number.add(this.generateFractionalPart());
                }
                return number;
            }
            if (this.multOrDivChosen && !this.addOrSubChosen) {
                let number = this.getNumberByCapacity(
                    sample(
                        this.parameters.multDivDigitCapacity.left.length
                            ? this.parameters.multDivDigitCapacity.left
                            : this.parameters.multDivDigitCapacity.right
                    )
                );
                if (this.parameters.fractions) {
                    number = number.add(this.generateFractionalPart());
                }
                return number;
            }
            return this.generateNumber();
        },
        generateFractionalPart() {
            if (this.parameters.fractionalPart === 1) {
                return new BigDecimal(Math.floor(Math.random() * 9 + 1)).divide(10);
            }
            return new BigDecimal(Math.floor(Math.random() * 90 + 10)).divide(100);
        },
        addBrackets(numbers) {
            let maxNumberAmountInBracket = Math.round(this.getRandomNumberFromInterval(1, numbers.length - 1));
            this.bracketCounter = Math.ceil(numbers.length / maxNumberAmountInBracket);

            let numberChunks = chunk(numbers, maxNumberAmountInBracket);

            this.bracketCounter = this.getBracketCounterWithoutBracketsOfOneElement(numberChunks, this.bracketCounter);

            if (this.parameters.bracketAmount < this.bracketCounter) {
                const extraBracketAmount = this.bracketCounter - this.parameters.bracketAmount;
                numberChunks = this.removeExtraBrackets(numberChunks, extraBracketAmount)
                    .numberChunksWithRemovedExtraBrackets;
            } else if (this.parameters.bracketAmount > this.bracketCounter) {
                this.increaseBracketDepth(numberChunks);
            }
            if (this.parameters.bracketAmount !== this.getBracketAmount(numberChunks)) {
                numberChunks = this.addBrackets(cloneDeep(numbers));
            }

            return numberChunks;
        },
        getRandomNumberFromInterval(min, max) {
            return Math.random() * (max - min) + min;
        },
        getBracketCounterWithoutBracketsOfOneElement(numberChunks, bracketCounter) {
            for (let i = 0; i < numberChunks.length; i++) {
                if (numberChunks[i].length === 1) {
                    bracketCounter--;
                }
            }
            return bracketCounter;
        },
        increaseBracketDepth(numberChunks) {
            if (this.bracketCounter === this.parameters.bracketAmount || typeof numberChunks === 'number') {
                return;
            }
            for (let i = 0; i < numberChunks.length; i++) {
                if (numberChunks[i].length > 2) {
                    let slicedArray;
                    let sliceAmount;
                    let maxNumberAmountInBracket;
                    do {
                        // EXTRA_WEIGHT to decrease a chance of 1 element bracket
                        maxNumberAmountInBracket = Math.round(
                            this.getRandomNumberFromInterval(1, numberChunks[i].length - 1) + EXTRA_WEIGHT
                        );
                        sliceAmount = Math.ceil(numberChunks[i].length / maxNumberAmountInBracket);
                        slicedArray = chunk(numberChunks[i], maxNumberAmountInBracket);

                        sliceAmount = this.getBracketCounterWithoutBracketsOfOneElement(slicedArray, sliceAmount);
                    } while (this.parameters.bracketAmount < this.bracketCounter + sliceAmount);

                    this.bracketCounter += sliceAmount;
                    numberChunks[i] = chunk(numberChunks[i], maxNumberAmountInBracket);

                    if (this.parameters.bracketAmount === this.bracketCounter) {
                        return;
                    }
                } else {
                    return;
                }
            }
            for (let i = 0; i < numberChunks.length; i++) {
                this.increaseBracketDepth(numberChunks[i]);
            }
        },
        removeExtraBrackets(numberChunks, extraBracketAmount) {
            let indicesToRemove = [];
            for (let i = 0; i < numberChunks.length; i++) {
                if (Array.isArray(numberChunks[i])) {
                    let { bracketToRemoveAmount, numberChunksWithRemovedExtraBrackets } = this.removeExtraBrackets(
                        numberChunks[i],
                        extraBracketAmount
                    );
                    indicesToRemove.push(i);
                    if (bracketToRemoveAmount === 0) {
                        numberChunks = [numberChunksWithRemovedExtraBrackets, ...numberChunks];
                        break;
                    }
                    numberChunks = [numberChunksWithRemovedExtraBrackets, ...numberChunks];
                    extraBracketAmount--;
                }
            }
            for (let i = indicesToRemove.length - 1; i >= 0; i--) {
                numberChunks.splice(indicesToRemove[i], 1);
            }
            return {
                bracketToRemoveAmount: extraBracketAmount,
                numberChunksWithRemovedExtraBrackets: numberChunks,
            };
        },
        getBracketAmount(numberChunks) {
            let bracketCounter = 0;
            for (let i = 0; i < numberChunks.length; i++) {
                if (Array.isArray(numberChunks[i]) && numberChunks[i].length > 1) {
                    bracketCounter += 1;
                    bracketCounter += this.getBracketAmount(numberChunks[i]);
                }
            }
            return bracketCounter;
        },
        makeDivisionOnlyTreeNonFractional(node) {
            if (!this.isComplexNode(node)) {
                return node;
            }

            for (let i = 0; i < node.childNodes.length; i++) {
                node.childNodes[i] = this.makeDivisionOnlyTreeNonFractional(node.childNodes[i]);
            }

            if (!this.areChildNodesDivisible(node.childNodes)) {
                let numberToMultiplyFirstChildNumberBy = new BigDecimal('1');
                for (let i = 1; i < node.childNodes.length; i++) {
                    numberToMultiplyFirstChildNumberBy = numberToMultiplyFirstChildNumberBy.multiply(
                        this.getNodeResult(node.childNodes[i])
                    );
                }

                node = this.multiplyNodeFirstChildNumber(node, numberToMultiplyFirstChildNumberBy);
            }

            return node;
        },
        areChildNodesDivisible(nodes) {
            let divisionResult = this.getNodeResult(nodes[0]);
            for (let i = 1; i < nodes.length; i++) {
                divisionResult = divisionResult.divide(this.getNodeResult(nodes[i]));

            }
            return !divisionResult.isFractional() && !divisionResult.isZero();
        },
        multiplyNodeFirstChildNumber(node, multiplier) {
            if (node.childNodes.length > 1) {
                if (!this.isComplexNode(node.childNodes[0])) {
                    node.childNodes[0] = node.childNodes[0].multiply(multiplier);
                } else {
                    let deeperFirstChildNode = node.childNodes[0];
                    deeperFirstChildNode.result = deeperFirstChildNode.result.multiply(multiplier);
                    while (this.isComplexNode(deeperFirstChildNode.childNodes?.[0])) {
                        deeperFirstChildNode = deeperFirstChildNode.childNodes[0];
                        deeperFirstChildNode.result = deeperFirstChildNode.result.multiply(multiplier);
                    }
                    deeperFirstChildNode.childNodes[0] = deeperFirstChildNode.childNodes[0].multiply(multiplier);
                }
                node.result = this.getNodeResult(node.childNodes[0]);
                for (let i = 1; i < node.childNodes.length; i++) {
                    node.result = node.result.divide(this.getNodeResult(node.childNodes[i]));
                }
            }
            return node;
        },
        convertToTree(numberChunks) {
            let node = this.getTreeNode();

            for (let i = 0; i < numberChunks.length; i++) {
                node.childNodes.push(
                    Array.isArray(numberChunks[i]) ? this.convertToTree(numberChunks[i]) : numberChunks[i]
                );

                const isFirstChildNode = i === 0;
                if (!isFirstChildNode) {
                    let operation = this.getOperation();

                    if (!this.parameters.fractions) {
                        const siblingNodesAreNumbers =
                            !this.isComplexNode(node.childNodes[i - 1]) && !this.isComplexNode(node.childNodes[i]);

                        const siblingNodesAreDivisible = !this.getNodeResult(node.childNodes[i - 1])
                            .divide(this.getNodeResult(node.childNodes[i]))
                            .isFractional();
                        const prevOperation = node.operations[i - 2];
                        if (siblingNodesAreNumbers && operation === '/' && prevOperation !== '/') {
                            [
                                node.childNodes[i - 1],
                                node.childNodes[i],
                            ] = this.changeSiblingNumbersForDivisionWithoutRemainder(prevOperation);
                        } else if (operation === '/') {
                            const OPERATION_TO_EXCLUDE = '/';
                            operation = this.getOperation(OPERATION_TO_EXCLUDE);
                        }
                        if (siblingNodesAreDivisible && !siblingNodesAreNumbers && this.parameters.operations.divide) {
                            operation = '/';
                        }
                    }

                    node.operations.push(operation);
                }
            }

            this.calculateNodeResult(node);
            return node;
        },
        convertToDivisionOnlyTree(numberChunks) {
            let node = this.getTreeNode();

            for (let i = 0; i < numberChunks.length; i++) {
                node.childNodes.push(
                    Array.isArray(numberChunks[i]) ? this.convertToDivisionOnlyTree(numberChunks[i]) : numberChunks[i]
                );

                const isFirstChildNode = i === 0;
                if (!isFirstChildNode) {
                    let operation = this.getOperation();

                    node.operations.push(operation);
                }
            }
            this.calculateNodeResult(node);
            if (!this.parameters.fractions) {
                node = this.makeDivisionOnlyTreeNonFractional(node);
            }

            return node;
        },
        changeSiblingNumbersForDivisionWithoutRemainder(prevOperation) {
            const getNumberCapacity = number => number.toString().replace(/-/, '').length;
            const maxNumberOfCapacity = capacity => +'9'.repeat(capacity);
            const minNumberOfCapacity = capacity => Math.pow(10, capacity - 1);
            const capacityForDevidened = capacity => {
                if (this.multOrDivChosen && !this.addOrSubChosen && prevOperation !== '*') {
                    return Math.max(...this.parameters.multDivDigitCapacity.left);
                } else {
                    return capacity
                }
            };
            let divisor;
            let dividend;

            const capacity = this.getCapacityByDifficult;
            let i = 9;
            do {
                divisor = new BigDecimal(
                    random(minNumberOfCapacity(capacity), Math.trunc(maxNumberOfCapacity(capacity) / 2))
                );
                dividend = divisor.multiply(random(2, Math.abs(i)));
                i--;
            } while (getNumberCapacity(dividend) !== capacityForDevidened(capacity));
            return [dividend, divisor];
        },
        getTreeNode() {
            const generator = this;
            return {
                childNodes: [],
                operations: [],
                result: 0,
                toString() {
                    return this.childNodes.reduce((stringRepresentation, node, index) => {
                        if (index !== 0) {
                            stringRepresentation += this.operations[index - 1];
                        }
                        if (generator.isComplexNode(node)) {
                            const { childNodes } = node;
                            const innerString = node.toString();
                            stringRepresentation += childNodes.length > 1 ? `(${innerString})` : innerString;
                        } else {
                            stringRepresentation += node;
                        }
                        return stringRepresentation;
                    }, '');
                },
            };
        },
        calculateNodeResult(node) {
            let operationsCopy = cloneDeep(node.operations);
            let childNodesCopy = cloneDeep(node.childNodes);
            if (this.multOrDivChosen) {
                this.calculateNodePriorityOperations(operationsCopy, childNodesCopy);
            }

            node.result = childNodesCopy.reduce((result, childNode, index) => {
                const isFirstNode = index === 0;
                if (isFirstNode) {
                    return this.getNodeResult(childNode);
                }
                let currentOperation = operationsCopy[index - 1];
                let currentResult = this.performOperation(result, this.getNodeResult(childNode), currentOperation);

                const needsToChangeOperation = !this.parameters.negativeAnswers && currentResult.isNegative();
                if (needsToChangeOperation) {
                    const indicesOfAddAndSubOperations = node.operations.reduce((operations, operation, index) => {
                        if (['-', '+'].includes(operation)) {
                            operations.push(index);
                        }
                        return operations;
                    }, []);
                    currentOperation = '+';
                    const indexOfOperationToChangeInInitialOperationArray = indicesOfAddAndSubOperations[index - 1];
                    node.operations[indexOfOperationToChangeInInitialOperationArray] = '+';
                    currentResult = this.performOperation(result, this.getNodeResult(childNode), currentOperation);
                }
                return currentResult;
            }, new BigDecimal('0'));
        },
        calculateNodePriorityOperations(operations, childNodes) {
            const PRIORITY_OPERATIONS = ['*', '/'];
            while (operations.find(operation => PRIORITY_OPERATIONS.includes(operation))) {
                const operation = operations.find(operation => PRIORITY_OPERATIONS.includes(operation));
                const index = operations.indexOf(operation);
                operations.splice(index, 1);

                let firstOperand = this.getNodeResult(childNodes[index]);
                let secondOperand = this.getNodeResult(childNodes[index + 1]);
                let operationResult = this.performOperation(firstOperand, secondOperand, operation);

                childNodes.splice(index, 2, operationResult);
            }
        },
        getNodeResult(node) {
            return this.isComplexNode(node) ? node.result : node;
        },
        getOperation(operationToExclude) {
            let operations = [];

            this.parameters.operations.add && operations.push('+');
            this.parameters.operations.subtract && operations.push('-');
            this.parameters.operations.multiply && operations.push('*');
            this.parameters.operations.divide && operations.push('/');

            operations = operations.filter(operation => operation !== operationToExclude);
            return sample(operations);
        },
        performOperation(firstOperand, secondOperand, operation) {
            firstOperand = new BigDecimal(firstOperand);
            secondOperand = new BigDecimal(secondOperand);
            switch (operation) {
                case '+':
                    return firstOperand.add(secondOperand);
                case '-':
                    return firstOperand.subtract(secondOperand);
                case '*':
                    return firstOperand.multiply(secondOperand);
                case '/':
                    return firstOperand.divide(secondOperand);
            }
        },
        changeCombiningPercents() {
            let combiningPercents = cloneDeep(defaultCombiningPercents);
            let capacityLimit = this.getCapacityByDifficult;
            const combiningDigitCapacities =
                this.multOrDivChosen && !this.addOrSubChosen
                    ? this.parameters.multDivDigitCapacity.right
                    : this.parameters.combiningDigitCapacities;

            if (combiningDigitCapacities.length >= MIN_ARR_LGTH_OF_DIGIT_CAP) {
                let capacitiesWhichNeedToResetChance = difference(
                    map(keys(combiningPercents[capacityLimit]), key => {
                        return +key;
                    }),
                    combiningDigitCapacities
                );
                if (capacitiesWhichNeedToResetChance.length) {
                    capacitiesWhichNeedToResetChance.forEach(capacity => {
                        combiningPercents[capacityLimit][capacity] = 0;
                    });
                    let newChancePercents = values(defaultCombiningPercents[combiningDigitCapacities.length]);
                    combiningDigitCapacities.forEach((capacity, index) => {
                        combiningPercents[capacityLimit][capacity] = newChancePercents[index];
                    });
                }
            }
            return combiningPercents;
        },
        generateCurrentDigitsLimit(digitsLimit, combiningPercents) {
            let currentDigitsLimit;
            let randomPercent = Math.floor(Math.random() * 100);
            let remainingPercent = 100;
            for (let i = digitsLimit; i > 0; i--) {
                if (randomPercent >= (remainingPercent -= combiningPercents[digitsLimit][i])) {
                    currentDigitsLimit = i;
                    return currentDigitsLimit;
                }
            }
        },
        getNumberByCapacity(capacity) {
            const startNumber = new BigDecimal('1'.padEnd(capacity + 1, '0'));
            const lastNumber = new BigDecimal('1'.padEnd(capacity, '0'));
            return new BigDecimal(
                BigDecimal.floor(
                    startNumber
                        .subtract(lastNumber)
                        .multiply(Math.random())
                        .add(lastNumber)
                )
            );
        },
        isComplexNode(valueToCheck) {
            const isBigDecimal = valueToCheck instanceof BigDecimal;
            return typeof valueToCheck === 'object' && valueToCheck !== null && !isBigDecimal;
        },
        removeTrailingZeros(number) {
            return number.toString().replace(/(\.[0-9]*[1-9])0+$|\.0*$/, '$1');
        },
    },
    computed: {
        multOrDivChosen() {
            return this.parameters.operations.multiply || this.parameters.operations.divide;
        },
        addOrSubChosen() {
            return this.parameters.operations.add || this.parameters.operations.subtract;
        },
        onlyDivisionChosen() {
            return (
                this.parameters.operations.divide &&
                Object.values(this.parameters.operations).filter(operation => operation).length === 1
            );
        },
        getCapacityByDifficult() {
            if (this.multOrDivChosen && !this.addOrSubChosen) {
                return Math.max(...this.parameters.multDivDigitCapacity.right);
            }
            if (this.parameters.difficult === 'Однозначные цифры') {
                return 1;
            } else if (this.parameters.difficult === 'Двузначные цифры') {
                return 2;
            } else if (this.parameters.difficult === 'Трехзначные цифры') {
                return 3;
            } else if (this.parameters.difficult === 'Четырехзначные цифры') {
                return 4;
            } else if (this.parameters.difficult === 'Пятизначные цифры') {
                return 5;
            } else if (this.parameters.difficult === 'Шестизначные цифры') {
                return 6;
            } else if (this.parameters.difficult === 'Семизначные цифры') {
                return 7;
            } else if (this.parameters.difficult === 'Восьмизначные цифры') {
                return 8;
            }
        },
    },
};
